import { Seat } from './seat';
import { IPosition } from './iposition';
import { PositionWrite } from './positionWrite';
import { Person } from 'src/app/people/models/person';
import { SeatStatus } from './seatStatus';
import _ from 'lodash';
import { TeamStatusCount } from './teamStatusCount';
import { PositionListing } from 'src/app/shared/models/positionListing';

export class Position implements IPosition {
  public id: string;
  public title: string;
  public description: string;
  public seats: Seat[];
  public teamId: string;
  public ranking: number;
  public seatCount: number;
  public benchSeatCount: number;
  public createdOn: Date;
  public updatedOn: Date;
  public positionListings: PositionListing[];
  public arePeopleSyncedToTeamManager: boolean;
  public eligibilityFilters: string[];
  public eligibilityODataQuery: string;

  constructor(
    id: string,
    title: string,
    seats: Seat[],
    description: string,
    teamId: string,
    ranking: number,
    seatCount: number,
    benchSeatCount: number,
    createdOn: Date,
    updatedOn: Date,
    arePeopleSyncedToTeamManager: boolean,
    positionListings: PositionListing[],
    eligibilityFilters: string[] = [],
    eligibilityODataQuery: string = null
  ) {
    this.id = id;
    this.title = title;
    this.description = description;
    this.teamId = teamId;
    this.ranking = ranking;
    this.seatCount = seatCount;
    this.benchSeatCount = benchSeatCount;
    this.createdOn = createdOn;
    this.updatedOn = updatedOn;
    this.seats = seats || Array<Seat>();
    this.positionListings = positionListings || Array<PositionListing>();
    this.arePeopleSyncedToTeamManager = arePeopleSyncedToTeamManager;
    this.eligibilityFilters = eligibilityFilters ?? [];
    this.eligibilityODataQuery = eligibilityODataQuery ?? null;
  }

  get statusCounts(): Array<TeamStatusCount> {
    let seatCounts = {
      picked: 0,
      accepted: 0,
      declined: 0,
      invited: 0,
      filled: 0,
      noResponse: 0,
      noShow: 0
    };

    let currentSeats = this.seats.filter((seat) => seat.isCurrent);

    _.forEach(currentSeats, (seat) => {
      switch (seat.status) {
        case SeatStatus.Accepted:
          seatCounts.accepted += 1;
          seatCounts.filled += 1;
          break;
        case SeatStatus.Declined:
          seatCounts.declined += 1;
          break;
        case SeatStatus.NoShow:
          seatCounts.noShow += 1;
          break;
        case SeatStatus.Picked:
          seatCounts.picked += 1;
          seatCounts.filled += 1;
          break;
        case SeatStatus.Invited:
          seatCounts.invited += 1;
          seatCounts.filled += 1;
          break;
        case SeatStatus.NoResponse:
          seatCounts.noResponse += 1;
          seatCounts.filled += 1;
          break;
      }
    });

    let empty = this.seatCount - seatCounts.filled;
    let positionCounts = new Array<TeamStatusCount>();
    positionCounts.push(new TeamStatusCount(SeatStatus.Picked, seatCounts.picked));
    positionCounts.push(new TeamStatusCount(SeatStatus.Invited, seatCounts.invited));
    positionCounts.push(new TeamStatusCount(SeatStatus.NoResponse, seatCounts.noResponse));
    positionCounts.push(new TeamStatusCount(SeatStatus.Declined, seatCounts.declined));
    positionCounts.push(new TeamStatusCount(SeatStatus.NoShow, seatCounts.noShow));
    positionCounts.push(new TeamStatusCount(SeatStatus.Accepted, seatCounts.accepted));
    positionCounts.push(new TeamStatusCount(null, empty > 0 ? empty : 0));

    return positionCounts;
  }

  get draftCount(): number {
    return this.seats.filter(
      (s) => s.person && s.status != SeatStatus.Declined && s.status != SeatStatus.NoShow
    ).length;
  }

  toPositionWrite(clone: boolean = false) {
    return new PositionWrite(
      this.title,
      this.seatCount,
      this.arePeopleSyncedToTeamManager,
      clone ? null : this.id,
      this.description,
      this.eligibilityFilters,
      this.eligibilityODataQuery
    );
  }

  isPicked(person: Person): boolean {
    return _.some(this.seats, (s) => {
      if (s.person) return s.person.id === person.id;
      else return false;
    });
  }

  isInvited(person: Person): boolean {
    return _.some(this.seats, (s) => {
      if (s.person && s.status === SeatStatus.Invited) return s.person.id === person.id;
      else return false;
    });
  }

  isNoResponse(person: Person): boolean {
    return _.some(this.seats, (s) => {
      if (s.person && s.status === SeatStatus.NoResponse) return s.person.id === person.id;
      else return false;
    });
  }

  getDrafted(): Array<Seat> {
    let seats = new Array<Seat>();
    seats.push(
      ..._.filter(
        this.seats,
        (seat) =>
          seat.status != SeatStatus.Declined &&
          seat.status != SeatStatus.NoShow &&
          seat.status != SeatStatus.Bench
      )
    );
    return seats;
  }

  getDeclined(): Array<Seat> {
    let seats = new Array<Seat>();
    seats.push(..._.filter(this.seats, (seat) => seat.status == SeatStatus.Declined));
    return seats;
  }

  getNoShow(): Array<Seat> {
    let seats = new Array<Seat>();
    seats.push(..._.filter(this.seats, (seat) => seat.status == SeatStatus.NoShow));
    return seats;
  }

  getSeat(person: Person): Seat {
    const isPicked = this.isPicked(person) || this.isNoResponse(person);
    if (isPicked) {
      return _.find(this.seats, (seat: Seat) => {
        if (seat.person && seat.person.id === person.id) return true;
        else return false;
      });
    }
    return null;
  }

  isDrafted(person: Person): boolean {
    return _.some(this.seats, (s) => {
      if (s.person) return s.person.id === person.id;
      else return false;
    });
  }

  seatsToFill(): number {
    return (
      this.seatCount -
      _.filter(
        this.seats,
        (seat) => seat.status != SeatStatus.Declined && seat.status != SeatStatus.NoShow
      ).length
    );
  }

  canDelete(): boolean {
    return !_.some(this.seats, function (seat) {
      return (
        seat.status === SeatStatus.Picked ||
        seat.status === SeatStatus.Invited ||
        seat.status === SeatStatus.NoResponse ||
        seat.status === SeatStatus.Accepted
      );
    });
  }

  reOrderSeats() {
    this.seats = Position.orderSeatCollection(this.seats);
  }

  private static orderSeatCollection(seats: Array<Seat>): Array<Seat> {
    return _.orderBy(seats, ['status', 'person.lastName', 'person.firstName']);
  }

  static parse(obj: Partial<Position>): Position {
    let seats = new Array<Seat>();

    if (obj.seats) {
      obj.seats.forEach((s) => {
        seats.push(Seat.parse(s));
      });
    }

    return new Position(
      obj.id,
      obj.title,
      Position.orderSeatCollection(seats),
      obj.description,
      obj.teamId,
      obj.ranking,
      obj.seatCount,
      obj.benchSeatCount,
      obj.createdOn,
      obj.updatedOn,
      obj.arePeopleSyncedToTeamManager,
      obj.positionListings,
      obj.eligibilityFilters,
      obj.eligibilityODataQuery
    );
  }
}
